
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UserWithSession {
  user_id: string;
  is_online: boolean;
  last_login: string | null;
  last_active: string | null;
  device_info: string;
}

export const useUserSessions = () => {
  const [userSessions, setUserSessions] = useState<UserWithSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchUserSessions = async () => {
    try {
      const { data: userStats, error } = await supabase
        .from('user_stats')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error("Erro ao buscar user_stats:", error);
        throw error;
      }

      const processedSessions: UserWithSession[] = userStats?.map(stat => {
        // Calcular status online baseado em last_active em tempo real
        const lastActiveDate = stat.last_active ? new Date(stat.last_active) : null;
        const now = new Date();
        const isOnline = lastActiveDate ? (now.getTime() - lastActiveDate.getTime()) < (2 * 60 * 1000) : false;
        
        return {
          user_id: stat.user_id,
          is_online: isOnline,
          last_login: stat.last_active,
          last_active: stat.last_active,
          device_info: stat.device_info || 'Desconhecido'
        };
      }) || [];
      
      setUserSessions(processedSessions);
      
    } catch (error) {
      console.error("Erro ao buscar user stats:", error);
      setUserSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserSessions();

    // Debounce timer para evitar múltiplas atualizações seguidas
    let debounceTimer: NodeJS.Timeout;

    // Configurar realtime para atualizações automáticas da tabela user_stats
    const channel = supabase
      .channel('user-stats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_stats'
        },
        (payload) => {
          // Debounce de 3 segundos para evitar atualizações excessivas
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            fetchUserSessions();
            toast({
              title: "📊 Sessões atualizadas",
              description: "Status de usuários online atualizado",
              duration: 3000,
            });
          }, 3000);
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  const getUserSessionInfo = (userId: string): UserWithSession | null => {
    return userSessions.find(session => session.user_id === userId) || null;
  };

  return {
    userSessions,
    isLoading,
    getUserSessionInfo,
    refetch: fetchUserSessions
  };
};
